import { useEffect } from "react";
import {
  Button,
  Divider,
  Stack,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  Paper,
  FormControl,
  IconButton,
  Grid,
  FormHelperText,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  TextField,
} from "@mui/material";
import Add from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import { useFormikContext, FieldArray } from "formik";
import { Condition } from "./Condition";
import { Validation } from "./Validation";
import { getFieldList } from "./utils/getFieldList";
import { logicalOperators, logicalOperatorRules } from "../../../../../constants";

const accumulationScopeMessages = {
  customer: "separately for each customer",
  entity: "separately for each entity",
};

const ConditionalField = ({ requiredFieldsList, sectionList, index, deleteField, isLast }) => {
  const { values, handleChange, handleBlur, errors, setErrors, setFieldValue } = useFormikContext();
  const { subSectionList, targetFieldList, hasSubSection } = getFieldList({
    requiredFieldsList,
    section: values.conditionalFields[index].section,
    subSection: values.conditionalFields[index].subSection,
  });
  const conditions = values.conditionalFields[index].conditions ?? [];

  // Reset validations when hasValidation becomes false.
  useEffect(() => {
    if (!values.conditionalFields[index].hasValidation) {
      setFieldValue(`conditionalFields.${index}.validations`, []);
    }
  }, [values.conditionalFields[index].hasValidation, index, setFieldValue]);

  return (
    <Stack flexDirection="column" rowGap={2} width="100%">
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={1}>
          <Typography fontWeight="bold">Show</Typography>
        </Grid>
        <Grid item xs={hasSubSection ? 3.33 : 5}>
          <FormControl fullWidth error={!!errors?.conditionalFields?.[index]?.section}>
            <InputLabel size="small" htmlFor={`conditionalFields.${index}.section`}>
              Section
            </InputLabel>
            <Select
              labelId={`conditionalFields.${index}.section`}
              label="Section"
              size="small"
              name={`conditionalFields.${index}.section`}
              value={values.conditionalFields[index].section}
              onChange={(e) => {
                handleChange(e);
                // Reset subSection and targetField when section changes.
                setFieldValue(`conditionalFields.${index}.subSection`, "self");
                setFieldValue(`conditionalFields.${index}.targetField`, undefined);
              }}
              onBlur={handleBlur}
              inputProps={{
                id: `conditionalFields.${index}.section`,
              }}
            >
              {sectionList.map((section) => (
                <MenuItem value={section} key={section}>
                  {section}
                </MenuItem>
              ))}
            </Select>
            {!!errors?.conditionalFields?.[index]?.section && (
              <FormHelperText>{errors?.conditionalFields?.[index]?.section}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        {hasSubSection && (
          <Grid item xs={3.33}>
            <FormControl fullWidth error={!!errors?.conditionalFields?.[index]?.subSection}>
              <InputLabel size="small" htmlFor={`conditionalFields.${index}.subSection`}>
                Sub Section
              </InputLabel>
              <Select
                labelId={`conditionalFields.${index}.subSection`}
                label="Sub Section"
                size="small"
                name={`conditionalFields.${index}.subSection`}
                value={values.conditionalFields[index].subSection}
                onChange={handleChange}
                onBlur={handleBlur}
                inputProps={{
                  id: `conditionalFields.${index}.subSection`,
                }}
              >
                {subSectionList.map((subSection) => (
                  <MenuItem value={subSection} key={subSection}>
                    {subSection}
                  </MenuItem>
                ))}
              </Select>
              {!!errors?.conditionalFields?.[index]?.subSection && (
                <FormHelperText>{errors?.conditionalFields?.[index]?.subSection}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        )}
        <Grid item xs={hasSubSection ? 3.33 : 5}>
          <FormControl fullWidth error={!!errors?.conditionalFields?.[index]?.targetField}>
            <InputLabel size="small" htmlFor={`conditionalFields.${index}.targetField`}>
              Target Field
            </InputLabel>
            <Select
              labelId={`conditionalFields.${index}.targetField`}
              label="Target Field"
              size="small"
              name={`conditionalFields.${index}.targetField`}
              value={values.conditionalFields[index].targetField}
              onChange={handleChange}
              onBlur={handleBlur}
              inputProps={{
                id: `conditionalFields.${index}.targetField`,
              }}
            >
              {targetFieldList.map((targetField) => (
                <MenuItem value={targetField} key={targetField}>
                  {targetField}
                </MenuItem>
              ))}
            </Select>
            {!!errors?.conditionalFields?.[index]?.targetField && (
              <FormHelperText>{errors?.conditionalFields?.[index]?.targetField}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={1}>
          <IconButton color="primary" onClick={() => deleteField()} size="small">
            <ClearIcon fontSize="small" />
          </IconButton>
        </Grid>
      </Grid>
      <FormControlLabel
        name={`conditionalFields.${index}.hasValidation`}
        value={values.conditionalFields[index].hasValidation}
        onChange={handleChange}
        onBlur={handleBlur}
        control={<Checkbox size="small" checked={values.conditionalFields[index].hasValidation} />}
        label="Do you want to add validations to the target field?"
      />
      <Paper variant="outlined" sx={{ padding: 2 }}>
        <Stack flexDirection="column" rowGap={2} alignItems="flex-start" width="100%">
          <Stack flexDirection="row" columnGap={2} alignItems="center" width="100%" justifyContent="space-between">
            <Stack flexDirection="column" rowGap={1}>
              <Typography fontWeight="bold">Conditions</Typography>
              <Typography variant="caption" color="text.secondary">
                When these conditions are true, the target field is required.
              </Typography>
            </Stack>
            <FormControl error={!!errors?.conditionalFields?.[index]?.conditionType}>
              <RadioGroup
                row
                name={`conditionalFields.${index}.conditionType`}
                value={values.conditionalFields[index].conditionType}
                onChange={handleChange}
                onBlur={handleBlur}
                id={`conditionalFields.${index}.conditionType`}
              >
                {logicalOperators.map((type) => (
                  <FormControlLabel key={type} value={type} control={<Radio size="small" />} label={type} />
                ))}
              </RadioGroup>
            </FormControl>
          </Stack>
          {conditions.length === 0 && (
            <Stack width="100%" rowGap={2}>
              {!!errors?.conditionalFields?.[index]?.conditions &&
                typeof errors?.conditionalFields?.[index]?.conditions === "string" && (
                  <Alert severity="error" onClose={() => setErrors({})}>
                    {errors?.conditionalFields?.[index]?.conditions}
                  </Alert>
                )}
              <Typography variant="body1">No conditions set. This condition will be treated as true.</Typography>
            </Stack>
          )}
          <FieldArray name={`conditionalFields.${index}.conditions`}>
            {({ push, remove }) => {
              return (
                <>
                  {conditions.map((_condition, conditionIndex) => {
                    return (
                      <Condition
                        key={conditionIndex}
                        requiredFieldsList={requiredFieldsList}
                        sectionList={sectionList}
                        index={index}
                        conditionIndex={conditionIndex}
                        deleteCondition={() => remove(conditionIndex)}
                      />
                    );
                  })}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      push({
                        rule: undefined,
                        ruleValue: undefined,
                        section: sectionList[0],
                        subSection: "self",
                        sourceField: undefined, // Field to check against
                      })
                    }
                    startIcon={<Add />}
                  >
                    Add Condition
                  </Button>
                </>
              );
            }}
          </FieldArray>
        </Stack>
      </Paper>
      {values.conditionalFields[index].hasValidation && (
        <Paper variant="outlined" sx={{ padding: 2 }}>
          <Stack flexDirection="column" rowGap={2} alignItems="flex-start" width="100%">
            <Stack flexDirection="row" columnGap={2} alignItems="center" width="100%" justifyContent="space-between">
              <Stack flexDirection="column" rowGap={1}>
                <Typography fontWeight="bold">Validations</Typography>
                <Typography variant="caption" color="text.secondary">
                  Set the positive or valid value rules for the target field here.
                </Typography>
              </Stack>
              <FormControl error={!!errors?.conditionalFields?.[index]?.validationType}>
                <RadioGroup
                  row
                  name={`conditionalFields.${index}.validationType`}
                  value={values.conditionalFields[index].validationType}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  id={`conditionalFields.${index}.validationType`}
                >
                  {logicalOperators.map((type) => (
                    <FormControlLabel key={type} value={type} control={<Radio size="small" />} label={type} />
                  ))}
                </RadioGroup>
              </FormControl>
            </Stack>
            <Stack width="100%" mb={1}>
              <FormControl fullWidth>
                <TextField
                  variant="outlined"
                  label="Custom validation Message"
                  size="small"
                  id={`conditionalFields.${index}.validationMessage`}
                  name={`conditionalFields.${index}.validationMessage`}
                  value={values.conditionalFields[index].validationMessage}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </FormControl>
            </Stack>
            {(values.conditionalFields[index].validations ?? []).length === 0 && (
              <Stack width="100%" rowGap={2}>
                <Typography variant="body1">
                  Validation rules define which values are accepted for the target field.
                </Typography>
              </Stack>
            )}
            <FieldArray name={`conditionalFields.${index}.validations`}>
              {({ push, remove }) => {
                return (
                  <>
                    {(values.conditionalFields[index].validations ?? []).map((validation, validationIndex) => {
                      const path = `conditionalFields.${index}.validations.${validationIndex}`;
                      return (
                        <Stack key={validationIndex} flexDirection="column" width="100%" rowGap={2}>
                          <Stack width="100%" spacing={0.5}>
                            <FormControl fullWidth>
                              <RadioGroup
                                row
                                sx={{
                                  "& .MuiFormControlLabel-root": { ml: -0.5, mr: 1.5 },
                                  "& .MuiFormControlLabel-label": { fontSize: "0.75rem" },
                                  "& .MuiRadio-root": { p: 0.5 },
                                  "& .MuiSvgIcon-root": { fontSize: 18 },
                                }}
                                id={`${path}.aggregation`}
                                aria-label="Evaluate"
                                name={`${path}.aggregation`}
                                value={validation.aggregation ?? "none"}
                                onChange={(event) => {
                                  const aggregation = event.target.value;
                                  const resetRule =
                                    aggregation === "sum" && !logicalOperatorRules.includes(validation.rule);
                                  setFieldValue(path, {
                                    ...validation,
                                    aggregation,
                                    ...(resetRule ? { rule: "", ruleValue: "" } : {}),
                                  });
                                }}
                                onBlur={handleBlur}
                              >
                                <FormControlLabel
                                  value="none"
                                  control={<Radio size="small" />}
                                  label="Individual value"
                                />
                                <FormControlLabel
                                  value="sum"
                                  control={<Radio size="small" />}
                                  label={`Accumulated total (Sums this field
                                  ${
                                    accumulationScopeMessages[values.conditionalFields[index].section] ??
                                    "separately for each parent"
                                  })`}
                                />
                              </RadioGroup>
                            </FormControl>
                            <Validation
                              index={index}
                              validationIndex={validationIndex}
                              deleteValidation={() => remove(validationIndex)}
                            />
                          </Stack>
                          {validationIndex < values.conditionalFields[index].validations.length - 1 && <Divider />}
                        </Stack>
                      );
                    })}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        push({
                          rule: undefined,
                          ruleValue: undefined,
                        })
                      }
                      startIcon={<Add />}
                    >
                      Add Validation
                    </Button>
                  </>
                );
              }}
            </FieldArray>
          </Stack>
        </Paper>
      )}
      {!isLast && <Divider sx={{ marginTop: 2 }} />}
    </Stack>
  );
};

export { ConditionalField };
